import "./style.css";
import { authLinkType, configured, rpc, sb } from "./sb.ts";
import { applyBrand, loadBrand } from "./brand.ts";
import { applyLang, t } from "./i18n.ts";
import { h, mount, toast } from "./ui.ts";
import { signIn } from "./pages/signin.ts";
import { setupWizard } from "./pages/setup.ts";
import { shell } from "./pages/shell.ts";
import { mfaChallenge, setPassword } from "./pages/account.ts";

const root = document.getElementById("app")!;
let mustSetPassword = authLinkType === "invite" || authLinkType === "recovery";

export async function route() {
  applyLang();
  if (!configured) {
    mount(root, h("main", { class: "card narrow" }, h("h1", {}, t("Not configured")),
      h("p", {}, t("Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY for this company's Supabase project, then build again."))));
    return;
  }
  const brand = await loadBrand();
  applyBrand(brand);
  const { data: { session } } = await sb.auth.getSession();
  shownUser = session?.user?.id ?? null;
  if (!session) return signIn(root, brand, route);
  // an invite / reset link: choose a password first
  if (mustSetPassword) return setPassword(root, brand, () => { mustSetPassword = false; route(); });
  // two-step sign-in: a user with a second factor enters the code before anything else
  const { data: aal } = await sb.auth.mfa.getAuthenticatorAssuranceLevel();
  if (aal && aal.nextLevel === "aal2" && aal.currentLevel !== "aal2") return mfaChallenge(root, brand, route);
  const role = await rpc<string | null>("my_role").catch(() => null);
  if (!role) {
    const first = await rpc<boolean>("needs_bootstrap").catch(() => false);
    mount(root, h("main", { class: "card narrow" },
      h("h1", {}, first ? t("Set up this company") : t("No access yet")),
      h("p", {}, first ? t("You are the first user of this project: become its admin and run the setup.")
        : t("An admin has to give you a role (admin, agent or viewer). If two-step sign-in is required, set it up under My account.")),
      first && h("button", { class: "primary", onclick: async () => {
        try { await rpc("bootstrap_admin"); route(); } catch (e) { toast((e as Error).message, true); } } }, t("Become admin")),
      !first && h("button", { onclick: () => { location.hash = "#account"; shell(root, brand, null, route); } }, t("My account")),
      h("button", { onclick: async () => { await sb.auth.signOut(); route(); } }, t("Sign out"))));
    return;
  }
  // "Skip for now" in the wizard (this browser only): the app without WhatsApp, e.g. to try the AI first
  const skipped = (() => { try { return localStorage.getItem("skip-wa-setup") === "1"; } catch { return false; } })();
  if (!brand.setup_done && !(skipped && role === "admin")) {
    if (role === "admin") return setupWizard(root, brand, route, () => {
      try { localStorage.setItem("skip-wa-setup", "1"); } catch { /* private mode */ }
      location.hash = "#ai";
      route();
    });
    mount(root, h("main", { class: "card narrow" }, h("h1", {}, t("Setup not finished")), h("p", {}, t("An admin needs to finish the setup first."))));
    return;
  }
  return shell(root, brand, role, route);
}

// re-draw on a real change only (SIGNED_IN also fires when another tab refreshes the session)
let shownUser: string | null | undefined;
sb.auth.onAuthStateChange((ev, s) => {
  const uid = s?.user?.id ?? null;
  if (ev === "PASSWORD_RECOVERY") { mustSetPassword = true; shownUser = uid; route(); }
  else if ((ev === "SIGNED_IN" || ev === "SIGNED_OUT") && uid !== shownUser) { shownUser = uid; route(); }
});
if ("serviceWorker" in navigator && import.meta.env.PROD) navigator.serviceWorker.register(import.meta.env.BASE_URL + "sw.js").catch(() => {});
route();
